// src/utils/fan-type-requirements.js
// 🎯 SINGLE SOURCE OF TRUTH for Fan Type Requirements + Helpers
// - Normalizes legacy values (expert → diehard, dedicatedFan → dedicated, etc.)
// - Provides safe getters that always return valid structures
// - Keeps requirements immutable at runtime

// Canonical fan types used in the app (post-normalization)
export const VALID_FAN_TYPES = ['casual', 'dedicated', 'diehard'];

/**
 * Immutable requirements per fan type.
 * If you need to change structure, do it here—everything else reads from this object.
 */
export const FAN_TYPE_REQUIREMENTS = Object.freeze({
  casual: Object.freeze({
    positions: Object.freeze(['QB', 'DB']), // Simplified: key positions
    coaches: Object.freeze(['HC']), // Head Coach only
    coachAreas: Object.freeze([
      'HC_leadership',
      'HC_gameManagement',
      'HC_playerDevelopment',
    ]),
    predictions: 3,
    timeEstimate: '5-10 minutes',
    description: 'Quick overview of key positions',
  }),

  dedicated: Object.freeze({
    // ✅ Includes TE per docs
    positions: Object.freeze(['QB', 'RB', 'WR', 'TE', 'DL', 'LB', 'DB']),
    coaches: Object.freeze(['HC', 'OC']), // HC + Offensive Coordinator
    coachAreas: Object.freeze([
      'HC_leadership',
      'HC_gameManagement',
      'HC_playerDevelopment',
      'OC_playCalling',
      'OC_schemeDesign',
      'OC_redZone',
    ]),
    predictions: 5,
    timeEstimate: '15-20 minutes',
    description: 'Detailed offensive analysis',
  }),

  // ✅ "diehard" is the canonical name (replaces legacy "expert")
  diehard: Object.freeze({
    positions: Object.freeze(['QB', 'RB', 'WR', 'TE', 'OL', 'DL', 'LB', 'DB', 'ST']), // Full analysis
    coaches: Object.freeze(['HC', 'OC', 'DC']), // All coordinators
    coachAreas: Object.freeze([
      'HC_leadership',
      'HC_gameManagement',
      'HC_playerDevelopment',
      'OC_playCalling',
      'OC_schemeDesign',
      'OC_redZone',
      'DC_strategy',
      'DC_personnel',
      'DC_adjustments',
    ]),
    predictions: 8,
    timeEstimate: '25-35 minutes',
    description: 'Complete team breakdown with all units',
  }),
});

/* -----------------------------------------------------------------------------
   Normalization / Legacy compatibility
----------------------------------------------------------------------------- */

/**
 * Normalize any incoming fanType to one of our canonical values.
 * Accepts undefined/null/weird casing without throwing; defaults to 'casual'.
 */
export const normalizeFanType = (fanType) => {
  if (!fanType || typeof fanType !== 'string') return 'casual';
  const raw = String(fanType).trim().toLowerCase();

  // Handle common legacy/frontend variations
  if (raw === 'expert' || raw === 'expertanalyst') return 'diehard';
  if (raw === 'dedicatedfan') return 'dedicated';

  // If already canonical, keep it
  if (VALID_FAN_TYPES.includes(raw)) return raw;

  // Unknowns default to diehard for completeness—or choose 'casual' if you prefer.
  // Using diehard matches prior behavior for "expert".
  return raw === 'pro' ? 'diehard' : 'diehard';
};

/**
 * Back-compat mapping (exported for places that still call a mapper explicitly).
 * Prefer `normalizeFanType` in new code.
 */
export const mapLegacyFanType = (fanType) => {
  const n = normalizeFanType(fanType);
  return n;
};

/**
 * Validate a fan type (after or before normalization).
 * Returns true if it’s either a canonical type or a supported legacy string.
 */
export const validateFanType = (fanType) => {
  const n = normalizeFanType(fanType);
  return VALID_FAN_TYPES.includes(n);
};

/* -----------------------------------------------------------------------------
   Requirement getters (safe fallbacks, no crashes)
----------------------------------------------------------------------------- */

const getReq = (fanType) => {
  const n = normalizeFanType(fanType);
  return FAN_TYPE_REQUIREMENTS[n] || FAN_TYPE_REQUIREMENTS.casual;
};

/** Positions required for the given fan type. */
export const getPositionsForFanType = (fanType) => getReq(fanType).positions;

/** Coach *areas* required for the given fan type. */
export const getCoachAreasForFanType = (fanType) => getReq(fanType).coachAreas;

/** Coach *categories/roles* required for the given fan type. */
export const getCoachCategoriesForFanType = (fanType) => getReq(fanType).coaches;

/** Number of predictions required for the given fan type. */
export const getPredictionsCountForFanType = (fanType) => getReq(fanType).predictions;

/** Estimated time string for the given fan type. */
export const getTimeEstimateForFanType = (fanType) => getReq(fanType).timeEstimate;

/** Description string for the given fan type. */
export const getDescriptionForFanType = (fanType) => getReq(fanType).description;

/* -----------------------------------------------------------------------------
   Extras
----------------------------------------------------------------------------- */

/** Quick helper to return the whole requirement block for a type. */
export const getRequirementsForFanType = (fanType) => getReq(fanType);

/** Export a frozen copy of valid requirement keys (useful for UI builders). */
export const REQUIREMENT_KEYS = Object.freeze([
  'positions',
  'coaches',
  'coachAreas',
  'predictions',
  'timeEstimate',
  'description',
]);
